const BlockType = require('../../extension-support/block-type');
const ArgumentType = require('../../extension-support/argument-type');
const Cast = require('../../util/cast');
                            
const RADIO_CHANNEL_VALUES = [
  {text:'bagpipes', value:'0'},
  {text:'bollywood', value:'1'},
  {text:'mariachi', value:'2'},
  {text:'drum n bass', value:'3'},
  {text:'car alarm', value:'4'},
  {text:'kung fu', value:'5'},
];

const RADIO_BLOCKS = [
  { opcode:'radio_start', text:'start radio', args:{}, anim:{op:'start_radio'} }, //B001
  { opcode:'radio_stop', text:'stop radio', args:{}, anim:{op:'stop_radio'} },    //B002
  { opcode:'radio_wait_half_loop', text:'wait for radio half loop', args:{}, anim:{op:'wait_half_loop'} }, //B003
  { opcode:'radio_change_channel', text:'change radio channel [CHANNEL]', args:{CHANNEL:{menu:'radioChannelsMenu', type:'number'}}, anim:{op:'change_radio_channel', name:'{CHANNEL}'} }, //B004
];

function argSpec(argKey, spec) {
  if (typeof spec === 'string') return { type: ArgumentType.STRING, menu: spec };
  const t = spec?.type === 'number' ? ArgumentType.NUMBER : ArgumentType.STRING;
  return spec?.menu ? { type: t, menu: spec.menu } : { type: t };
}

function interpolate(tpl, args) {
  return String(tpl).replace(/\{(\w+)\}/g, (_, k) => String(args[k] ?? '').replace(/\s+/g,'_'));
}

function toNumberMaybe(v) {
  const n = Number(v); return Number.isFinite(n) ? n : undefined;
}

const buildBlocks = (rawBlocks) => rawBlocks.map(b => ({
    opcode: b.opcode,
    blockType: BlockType.COMMAND,
    text: b.text,
    arguments: Object.fromEntries(Object.entries(b.args || {}).map(([k, spec]) => ([k, argSpec(k, spec)])))
}));

function getBlockHandler(b) {
    return function(args) { // Use 'function' to bind 'this' correctly
        const op = b.anim.op || 'play_animation';
        if (op === 'play_animation') {
            const name = interpolate(b.anim.name, args);
            const variant = b.anim.variant ? interpolate(b.anim.variant, args) : 'default';
            const loops = typeof b.anim.loops === 'string' ? toNumberMaybe(args[b.anim.loops.replace(/[{}]/g, '')]) ?? 1 : (b.anim.loops ?? 1);
            const payload = `{"animName":"${name}","state":"true","variantKey":"${variant}","loops":${loops}}`;
            return this.sendWaitAck({ OP: op, PAYLOAD: payload, TIMEOUT: -1 });
        } else if (op === 'change_radio_channel') {
            const ch = interpolate(b.anim.name, args);
            const payload = `{"animName":"${ch}","state":"true","variantKey":"","loops":1}`;
            return this.sendWaitAck({ OP: op, PAYLOAD: payload, TIMEOUT: -1 });
        } else if (op === 'joke_1' || op === 'joke_2') {
            const name = b.anim.name;
            const variant = interpolate(b.anim.variant, args);
            const payload = `{"animName":"${name}","state":"true","variantKey":"${variant}","loops":1}`;
            return this.sendWaitAck({ OP: op, PAYLOAD: payload, TIMEOUT: -1 });
        } else {
            const payload = `{}`;
            return this.sendWaitAck({ OP: op, PAYLOAD: payload, TIMEOUT: -1 });
        }
    };
}

class Scratch3CodematesMusic {
    constructor(runtime) {
        this.runtime = runtime;
        this._pending = new Map();
        this._events = new Map();
        this._awaiters = new Map();
        this._onUnityMessage = this._onUnityMessage.bind(this);
        window.addEventListener('unity-message', this._onUnityMessage);
        this.installHandlers(RADIO_BLOCKS);
    }

    initWebGL() { this.sendNow({ OP:'unity_init' }); }
    resetWebGL() { this.sendNow({ OP:'unity_cleanUp' }); }
    
    getInfo() {
        return {
            id: 'codematesMusic',
            name: '📻 Patch\'s Music',
            color1: '#4A90E2', // Blue
            blocks: buildBlocks(RADIO_BLOCKS),
            menus: {
                radioChannelsMenu: { acceptReporters: false, items: RADIO_CHANNEL_VALUES },
            }
        };
    }
    
    installHandlers(blocks) {
        for (const b of blocks) { this[b.opcode] = getBlockHandler(b).bind(this); }
    }

    // RPC Logic
    sendNow(args) {
        window.__codemates_seq = window.__codemates_seq || 0;
        const op = Cast.toString(args.OP);
        const payload = this._parse(Cast.toString(args.PAYLOAD || '{}'));
        this._post({ seq: ++this._seq, op, data: payload });
    }

    sendWaitAck(args) {
        const op = Cast.toString(args.OP);
        const payload = this._parse(Cast.toString(args.PAYLOAD));
        const n = Number(args.TIMEOUT);
        const timeout = Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
        return this._rpc(op, payload, { waitFor: 'ack', timeout });
    }

    _rpc(op, payload = {}, { timeout = 2000, waitFor } = {}) {
        window.__codemates_seq = window.__codemates_seq || 0;
        const seq = ++window.__codemates_seq;
        const body = { seq, op, data: payload };
        const p = new Promise((resolve, reject) => {
            let t = null;
            if (timeout) {
                t = setTimeout(() => { this._pending.delete(seq); resolve({ seq, op, timeout: true }); }, timeout);
            }
            this._pending.set(seq, { resolve, reject, t, waitFor });
        });
        this._post(body);
        return p;
    }

    _post(body) {
        if (typeof window.sendToUnity === 'function') { try { window.sendToUnity(body); } catch {} }
        else { (window.__unityQueue = window.__unityQueue || []).push(body); }
    }

    _parse(s) {
        try { const o = JSON.parse(s); return typeof o === 'object' && o !== null ? o : { value: s }; }
        catch { return { value: s }; }
    }

    _queueEvent(name, data) {
        const q = this._events.get(name) || [];
        q.push(data);
        this._events.set(name, q);
        const aw = this._awaiters.get(name);
        if (aw && aw.length) aw.shift()();
        this.runtime.startHats('codemates_whenUnityEvent', { EVENT: name });
    }

    _popEvent(name) {
        const q = this._events.get(name) || [];
        if (!q.length) return false;
        q.shift();
        return true;
    }

    _waitForEvent(name) {
        if (this._popEvent(name)) return Promise.resolve();
        return new Promise(r => {
            const list = this._awaiters.get(name) || [];
            list.push(r);
            this._awaiters.set(name, list);
        });
    }

    _onUnityMessage(evt) {
        const msg = evt && (evt.detail || evt.data);
        if (!msg || typeof msg !== 'object') return;
        if (msg.type === 'event' && msg.name) { this._queueEvent(msg.name, msg); return; }
        if (msg.seq && this._pending.has(msg.seq)) {
            const entry = this._pending.get(msg.seq);
            if (entry.t) clearTimeout(entry.t);
            if (!entry.waitFor || entry.waitFor === msg.type || msg.type === 'error') {
                this._pending.delete(msg.seq);
                if (msg.type === 'error') entry.reject(new Error(msg.error || 'unity error'));
                else entry.resolve(msg);
            }
        }
    }
}


module.exports = Scratch3CodematesMusic;
